/**
 * Client for crm-email's operations (segments_*, email_*, broadcasts_*; CONTRACTS §7, §7a).
 * Each call uses the real operation when GET /api/v1/ops lists it; otherwise the sample store in
 * stubs.ts (this browser only, badged "sample"). `sample` on every result says which one ran.
 */
import { api, ApiError, qs } from "./api";
import { mountainToISO } from "./format";
import { hasOp, sampleCount, samplePreview, sampleStore, saveSampleStore } from "./stubs";
import type { Broadcast, BroadcastPreview, EmailDoc, EmailPreview, EmailTopic, Segment, SegmentCounts, SegmentDefinition, SegmentMember } from "./types";

export type Sampled<T> = { data: T; sample: boolean };

const now = () => new Date().toISOString();

// ---- segments ----

export async function listSegments(p: { brand_id?: number; q?: string; include_archived?: boolean } = {}): Promise<Sampled<Segment[]>> {
  if (await hasOp("segments_list")) {
    const r = await api<{ segments: Segment[] }>("/api/v1/segments" + qs({ ...p, limit: 500 }));
    return { data: r.segments, sample: false };
  }
  const s = sampleStore();
  return { data: s.segments.filter((x) => (p.include_archived || !x.archived) && (!p.brand_id || x.brand_id === p.brand_id)), sample: true };
}

export async function getSegment(id: number): Promise<Sampled<Segment>> {
  if (await hasOp("segments_get")) return { data: await api<Segment>(`/api/v1/segments/${id}`), sample: false };
  const seg = sampleStore().segments.find((x) => x.id === id);
  if (!seg) throw new ApiError(404, "not_found", `Segment ${id} not found.`);
  return { data: seg, sample: true };
}

export async function saveSegment(v: { id?: number; name: string; brand_id?: number; description?: string; definition: SegmentDefinition }): Promise<Sampled<Segment>> {
  if (await hasOp("segments_create")) {
    const body = { name: v.name, brand_id: v.brand_id ?? 0, description: v.description ?? "", definition: v.definition };
    const data = v.id ? await api<Segment>(`/api/v1/segments/${v.id}`, { method: "PATCH", json: body }) : await api<Segment>("/api/v1/segments", { json: body });
    return { data, sample: false };
  }
  const s = sampleStore();
  const counts = await sampleCount(v.definition);
  const seg: Segment = {
    id: v.id ?? s.next++,
    name: v.name,
    brand_id: v.brand_id ?? null,
    description: v.description,
    definition: v.definition,
    last_count: counts?.matching ?? null,
    last_mailable: counts?.mailable ?? null,
    last_counted_at: counts ? now() : null,
    archived: false,
    updated_at: now(),
  };
  s.segments = [...s.segments.filter((x) => x.id !== seg.id), seg];
  saveSampleStore(s);
  return { data: seg, sample: true };
}

export async function archiveSegment(id: number): Promise<void> {
  if (await hasOp("segments_archive")) {
    await api(`/api/v1/segments/${id}/archive`, { method: "POST" });
    return;
  }
  const s = sampleStore();
  s.segments = s.segments.map((x) => (x.id === id ? { ...x, archived: true } : x));
  saveSampleStore(s);
}

export async function countSegment(v: { segment_id?: number; definition?: SegmentDefinition; brand_id?: number }): Promise<Sampled<SegmentCounts | null>> {
  if (await hasOp("segments_count")) return { data: await api<SegmentCounts>("/api/v1/segments/count", { json: v }), sample: false };
  const d = v.definition ?? sampleStore().segments.find((x) => x.id === v.segment_id)?.definition;
  return { data: d ? await sampleCount(d) : null, sample: true };
}

export async function segmentMembers(v: { segment_id?: number; definition?: SegmentDefinition; brand_id?: number; limit?: number }): Promise<Sampled<SegmentMember[]>> {
  if (await hasOp("segments_members")) {
    const r = await api<{ members: SegmentMember[] }>("/api/v1/segments/members", { json: { ...v, limit: v.limit ?? 10 } });
    return { data: r.members, sample: false };
  }
  return { data: [], sample: true };
}

// ---- email documents and previews ----

export interface EmailDraft {
  brand_id: number;
  name: string;
  subject: string;
  preheader?: string;
  from_name?: string;
  from_email?: string;
  reply_to?: string;
  html: string;
  footer?: "auto" | "none";
}

export async function getEmail(id: number): Promise<Sampled<EmailDoc>> {
  if (await hasOp("email_get")) return { data: await api<EmailDoc>(`/api/v1/emails/${id}?include_html=true`), sample: false };
  const e = sampleStore().emails.find((x) => x.id === id);
  if (!e) throw new ApiError(404, "not_found", `Email ${id} not found.`);
  return { data: e, sample: true };
}

/** email_topics_list for one brand (empty on an older server). */
export async function listTopics(brandId: number): Promise<EmailTopic[]> {
  if (!(await hasOp("email_topics_list"))) return [];
  const r = await api<{ topics: EmailTopic[] }>("/api/v1/email/topics" + qs({ brand_id: brandId }));
  return r.topics ?? [];
}

/** email_label_set: change an email's kind and/or topic (reason required; confirm to make it transactional). */
export async function setEmailLabel(emailId: number, v: { kind?: "marketing" | "transactional"; topic_id?: number; reason: string; confirm?: boolean }): Promise<EmailDoc> {
  return api<EmailDoc>(`/api/v1/emails/${emailId}/label`, { json: { ...v, confirm: v.confirm || undefined } });
}

/** email_topics_assign: one email's topic (0 = none). */
export async function assignTopic(emailId: number, topicId: number): Promise<void> {
  await api("/api/v1/email/topics/assign", { json: { email_ids: [emailId], topic_id: topicId } });
}

export async function saveEmail(id: number | null, d: EmailDraft): Promise<Sampled<EmailDoc>> {
  if (await hasOp("email_create")) {
    const data = id
      ? await api<EmailDoc>(`/api/v1/emails/${id}`, { method: "PATCH", json: { name: d.name, subject: d.subject, preheader: d.preheader, from_name: d.from_name || undefined, from_email: d.from_email || undefined, reply_to: d.reply_to || undefined, html: d.html, footer: d.footer } })
      : await api<EmailDoc>("/api/v1/emails", { json: { ...d, kind: "broadcast", generator: "admin" } });
    return { data, sample: false };
  }
  const s = sampleStore();
  const prev = id ? s.emails.find((x) => x.id === id) : undefined;
  const doc: EmailDoc = {
    id: id ?? s.next++,
    brand_id: d.brand_id,
    name: d.name,
    kind: "broadcast",
    settings: { footer: d.footer ?? "auto" },
    version: {
      id: s.next++,
      version: (prev?.version.version ?? 0) + 1,
      subject: d.subject,
      preheader: d.preheader,
      from_name: d.from_name || "Frank and Dave",
      from_email: d.from_email || "",
      reply_to: d.reply_to,
      merge_tokens: [...new Set(d.html.match(/~[A-Za-z]+\.[A-Za-z0-9_]+~/g) ?? [])],
      created_at: now(),
      created_by: "you (sample)",
      html: d.html,
    },
    links: [...d.html.matchAll(/href="(https?:[^"]+)"/g)].map((m, i) => ({ id: i + 1, position: i + 1, kind: "link", original_url: m[1], destination_url: m[1], edited: false })),
    warnings: [],
  };
  s.emails = [...s.emails.filter((x) => x.id !== doc.id), doc];
  saveSampleStore(s);
  return { data: doc, sample: true };
}

/** Exact previews (light + dark, phone + desktop) as images from the server's renderer. */
export async function renderPreview(v: { email_id?: number; html?: string; brand_id?: number; subject?: string; preheader?: string; contact_id?: number; dark_client?: string; kind?: string }): Promise<EmailPreview | null> {
  if (!(await hasOp("email_preview_render"))) return null;
  return api<EmailPreview>("/api/v1/emails/preview", { json: { ...v, layout: "separate", widths: ["phone", "desktop"] } });
}

export interface TestRecipients {
  test_list: string[];
  saved_extras: string[];
  max_extras: number;
}

export interface TestSendPreview {
  what?: string;
  email?: string;
  subject?: string;
  test_list?: string[];
  extra_people?: string[];
  note?: string;
}

export type TestSendResult =
  | { kind: "sent"; results: { to?: string; queued: boolean; skipped?: string }[] }
  | { kind: "confirm"; preview: TestSendPreview; message: string }
  | { kind: "sample" };

/** email_test_recipients_list: Brandon's test list and his saved extra people (null on an older server). */
export async function getTestRecipients(): Promise<TestRecipients | null> {
  if (!(await hasOp("email_test_recipients_list"))) return null;
  return api<TestRecipients>("/api/v1/email/test-recipients");
}

/** email_send_test. With extra people, the first call (confirm=false) answers confirm_required with a preview. */
export async function sendTest(emailId: number, p: { to: string[]; extra?: string[]; confirm?: boolean; subject?: string }): Promise<TestSendResult> {
  if (!(await hasOp("email_send_test"))) return { kind: "sample" };
  try {
    const r = await api<{ results: { to?: string; queued: boolean; skipped?: string }[] }>(`/api/v1/emails/${emailId}/test`, {
      json: { id: emailId, to: p.to.length ? p.to : undefined, extra: p.extra?.length ? p.extra : undefined, confirm: p.confirm || undefined, subject: p.subject || undefined },
    });
    return { kind: "sent", results: r.results ?? [] };
  } catch (e) {
    if (e instanceof ApiError && e.code === "confirm_required") return { kind: "confirm", preview: (e.details ?? {}) as TestSendPreview, message: e.message };
    throw e;
  }
}

// ---- broadcasts ----

export async function listBroadcasts(p: { brand_id?: number; status?: string } = {}): Promise<Sampled<Broadcast[]>> {
  if (await hasOp("broadcasts_list")) {
    const r = await api<{ broadcasts: Broadcast[] }>("/api/v1/broadcasts" + qs({ ...p, limit: 200 }));
    return { data: r.broadcasts, sample: false };
  }
  return { data: sampleStore().broadcasts.filter((b) => (!p.brand_id || b.brand_id === p.brand_id) && (!p.status || b.status === p.status)), sample: true };
}

export async function getBroadcast(id: number): Promise<Sampled<Broadcast>> {
  if (await hasOp("broadcasts_get")) return { data: await api<Broadcast>(`/api/v1/broadcasts/${id}`), sample: false };
  const b = sampleStore().broadcasts.find((x) => x.id === id);
  if (!b) throw new ApiError(404, "not_found", `Broadcast ${id} not found.`);
  return { data: b, sample: true };
}

export async function saveBroadcast(
  id: number | null,
  v: { name: string; email_id: number; segment_id?: number; definition?: SegmentDefinition; brand_id: number; subject_b?: string },
): Promise<Sampled<Broadcast>> {
  if (await hasOp("broadcasts_create")) {
    const data = id
      ? await api<Broadcast>(`/api/v1/broadcasts/${id}`, {
          method: "PATCH",
          json: { name: v.name, email_id: v.email_id, segment_id: v.segment_id, definition: v.definition, subject_b: v.subject_b ?? "" },
        })
      : await api<Broadcast>("/api/v1/broadcasts", { json: { ...v, subject_b: v.subject_b || undefined } });
    return { data, sample: false };
  }
  const s = sampleStore();
  const prev = id ? s.broadcasts.find((x) => x.id === id) : undefined;
  const b: Broadcast = {
    id: id ?? s.next++,
    name: v.name,
    brand_id: v.brand_id,
    email_id: v.email_id,
    email_version_id: null,
    status: prev?.status ?? "draft",
    segment_id: v.segment_id ?? null,
    definition: v.definition,
    scheduled_at: prev?.scheduled_at ?? null,
    started_at: null,
    completed_at: null,
    recipients_count: null,
    stats: {},
    approved_by: null,
    waiting_in_outbox: 0,
  };
  s.broadcasts = [...s.broadcasts.filter((x) => x.id !== b.id), b];
  saveSampleStore(s);
  return { data: b, sample: true };
}

/** Step 1 (confirm=false): the server answers confirm_required with what would happen.
 *  Step 2 (confirm=true, after the person says yes): schedules it. */
export async function scheduleBroadcast(id: number, sendAt: string, confirm: boolean): Promise<Sampled<{ preview?: BroadcastPreview; broadcast?: Broadcast }>> {
  if (await hasOp("broadcasts_schedule")) {
    try {
      const b = await api<Broadcast>(`/api/v1/broadcasts/${id}/schedule`, { json: { send_at: sendAt, confirm } });
      return { data: { broadcast: b }, sample: false };
    } catch (e) {
      if (e instanceof ApiError && e.code === "confirm_required") return { data: { preview: e.details as BroadcastPreview }, sample: false };
      throw e;
    }
  }
  const s = sampleStore();
  const b = s.broadcasts.find((x) => x.id === id);
  if (!b) throw new ApiError(404, "not_found", `Broadcast ${id} not found.`);
  if (!confirm) {
    const e = s.emails.find((x) => x.id === b.email_id);
    const d = b.definition ?? s.segments.find((x) => x.id === b.segment_id)?.definition;
    return { data: { preview: samplePreview(b, e, d ? await sampleCount(d) : null, sendAt) }, sample: true };
  }
  const updated: Broadcast = { ...b, status: "scheduled", scheduled_at: sendAt === "now" ? now() : mountainToISO(sendAt.slice(0, 10), sendAt.slice(11, 16)), approved_by: "you (sample)" };
  s.broadcasts = s.broadcasts.map((x) => (x.id === id ? updated : x));
  saveSampleStore(s);
  return { data: { broadcast: updated }, sample: true };
}

export async function broadcastAction(id: number, action: "pause" | "resume" | "cancel"): Promise<Sampled<Broadcast>> {
  if (await hasOp(`broadcasts_${action}`)) return { data: await api<Broadcast>(`/api/v1/broadcasts/${id}/${action}`, { method: "POST" }), sample: false };
  const s = sampleStore();
  const status = action === "pause" ? "paused" : action === "resume" ? "scheduled" : "cancelled";
  s.broadcasts = s.broadcasts.map((x) => (x.id === id ? { ...x, status } : x));
  saveSampleStore(s);
  return { data: s.broadcasts.find((x) => x.id === id)!, sample: true };
}
